import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/shadcn/card";
import { Icon } from "@iconify-icon/solid";
import { LoadingButton } from "../../components/ui/Button";
import { SelectInput } from "../../components/ui/form/SelectInput";
import { Badge } from "../../components/ui/Badge";
import useCheckupDiagnosis from "../../services/checkup/useCheckupDiagnosis";
import { createSignal, onMount, Show, For, createEffect } from "solid-js";
import { Button } from "@/components/shadcn/button";

export default function CheckupDiagnosis(props) {

  const { diagnoses, diagnosisForm, onSaveDiagnosis, handleDiagnosisInput, onFetchDiagnoses, statuses, addDiagnosis, removeDiagnosis, updateDiagnosisStatus } = useCheckupDiagnosis(props);

  onMount(() => {
    onFetchDiagnoses();
    // Register save function with parent component
    if (props.onRegisterSave) {
      props.onRegisterSave(onSaveDiagnosis);
    }
  });

  const onFormChange = () => {
    const originalDiagnoses = props.diagnoses || [];
    const currentDiagnoses = diagnosisForm.diagnoses || [];

    // Compare diagnoses to see if there are changes
    const hasChanges = originalDiagnoses.length !== currentDiagnoses.length;
    if (hasChanges) {
      props.onFormChange(true);
      return;
    }

    const diagnoses = JSON.parse(JSON.stringify(diagnosisForm.diagnoses || []));

    let hasStatusChanges = false;
    diagnoses.forEach(diagnosis => {
      // Ensure each diagnosis has an originalStatus for comparison
      if (diagnosis.originalStatus != diagnosis.status) {
        hasStatusChanges = true;
      }
    });

    props.onFormChange(hasStatusChanges);
  }

  // Track form changes - compare with original diagnoses
  createEffect(() => {
    if (props.onFormChange) {
      onFormChange();
    }
  });

  const content = (
    <>
      <Show when={!props.hideCard}>
        <CardHeader>
          <CardTitle class="flex items-center gap-2">
            <Icon icon="mdi:stethoscope" class="text-gray-500 me-2" />
            Diagnosis
          </CardTitle>
        </CardHeader>
      </Show>
      <div class={props.hideCard ? "space-y-4" : ""}>
        {/* Add New Diagnosis */}
        <div class="pt-0">
          <h4 class="font-medium text-gray-900 mb-3 dark:text-gray-100">
            Diagnoses
          </h4>
          <div class="flex gap-2 mb-4">
            <div class="flex-1">
              <SelectInput
                label="Select Diagnosis"
                name="diagnosisId"
                form={diagnosisForm}
                options={diagnoses()}
                input={handleDiagnosisInput}
                placeholder="Choose a diagnosis..."
              />
            </div>
            <div class="flex items-end">
              <Button variant="outline" onClick={addDiagnosis}>
                Add Diagnosis
              </Button>
            </div>
          </div>
          
          {/* All Diagnoses List */}
          <Show
            when={
              diagnosisForm.diagnoses &&
              diagnosisForm.diagnoses.length > 0
            }
          >
            <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
              <For each={diagnosisForm.diagnoses}>
                {(diagnosis) => (
                  <div class="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border dark:bg-gray-800 dark:border-gray-700">
                    <div class="flex-1">
                      <p class="font-medium text-sm">{diagnosis.name}</p>
                    </div>
                    <div class="flex items-center gap-2">
                      {/* Show status dropdown only if original status was "in progress" */}
                      <Show when={diagnosis.originalStatus === "in progress"}>
                        <SelectInput
                          name="status"
                          value={diagnosis.status}
                          input={(name, value) =>
                            updateDiagnosisStatus(diagnosis.key, value)
                          }
                          options={statuses()}
                          class="w-32 text-xs"
                        />
                      </Show>

                      {/* Show current status as badge if not editable */}
                      <Show when={diagnosis.originalStatus !== "in progress"}>
                        <Badge variant="outline" class="text-xs capitalize">
                          {diagnosis.status}
                        </Badge>
                      </Show>

                      {/* Show delete button only for new diagnoses, preserve space for uniform design */}
                      <div class="w-8 h-8 flex items-center justify-center">
                        <Show when={diagnosis.status === "new"}>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeDiagnosis(diagnosis.key)}
                            class="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 w-8 h-8"
                          >
                            <Icon icon="material-symbols-light:delete-outline-rounded" class="w-4 h-4" />
                          </Button>
                        </Show>
                      </div>
                    </div>
                  </div>
                )}
              </For>
            </div>
          </Show>
          <Show
            when={
              !diagnosisForm.diagnoses ||
              diagnosisForm.diagnoses.length === 0
            }
          >
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-4">
              No diagnoses assigned yet
            </p>
          </Show>

          <Show when={!props.hideSaveButton}>
            <LoadingButton
              loading={props.loading}
              onClick={onSaveDiagnosis}
              disabled={!diagnosisForm.diagnoses}
              class="mt-4"
            >
              Save Diagnoses
            </LoadingButton>
          </Show>
        </div>
      </div>
    </>
  );

  return (
    <Show when={props.hideCard} fallback={<Card><CardContent class="space-y-4">{content}</CardContent></Card>}>
      {content}
    </Show>
  );
}
